import React from 'react';
import LoadingCard from './LoadingCard';

export interface LoadingListProps {
  /** How many placeholder rows. Match the density of what is loading, roughly. */
  rows?: number;
  /** Text lines per row. 2 suits a title + meta row; 3 suits a card. */
  lines?: number;
  showAvatar?: boolean;
  /**
   * `card` gives each row the card treatment — right when the skeleton stands in
   * for a list of cards on the page background.
   *
   * `plain` draws bare rows with a hairline between them, for a skeleton that sits
   * INSIDE an existing card: a table body, an explorer panel. LoadingCard is a
   * card, so using it there would nest two, which the surface rule forbids.
   */
  surface?: 'card' | 'plain';
  /** Announced to assistive tech. Say what is loading — "Loading tracked bills". */
  label?: string;
  className?: string;
}

/**
 * P2-05. A panel that blanks to a centred spinner throws its layout away and then
 * snaps it back; a skeleton holds the shape, so a dense table stops jumping when
 * the data lands.
 *
 * This exists rather than repeating `Array.from(...).map(() => <LoadingCard />)`
 * at every call site, and — more importantly — because the spinners it replaces
 * carried `role="status"` from P2-04. Dropping straight to skeleton markup would
 * have silently un-announced every one of those panels. The status role and label
 * live here; the placeholder rows are decorative and hidden from the tree.
 */
const LoadingList: React.FC<LoadingListProps> = ({
  rows = 3,
  lines = 2,
  showAvatar = false,
  surface = 'card',
  label = 'Loading',
  className = '',
}) => (
  <div
    role="status"
    aria-live="polite"
    aria-label={label}
    className={`${surface === 'card' ? 'space-y-4' : ''} ${className}`}
  >
    <span className="sr-only">{label}</span>
    <div aria-hidden="true" className={surface === 'card' ? 'space-y-4' : ''}>
      {Array.from({ length: rows }).map((_, index) =>
        surface === 'card' ? (
          <LoadingCard key={index} lines={lines} showAvatar={showAvatar} />
        ) : (
          <div
            key={index}
            className="flex animate-pulse items-center gap-4 border-b border-border-light px-5 py-3.5 last:border-b-0 dark:border-border-dark-hairline"
          >
            <div className="h-4 flex-1 rounded bg-border-light/60 dark:bg-border-dark/60" />
            <div className="h-4 w-1/5 rounded bg-border-light/60 dark:bg-border-dark/60" />
            <div className="h-4 w-1/6 rounded bg-border-light/60 dark:bg-border-dark/60" />
          </div>
        )
      )}
    </div>
  </div>
);

export default LoadingList;
